import { useEffect, useRef, useState } from 'react';
import { getSocket } from '../socket';

// Real-time dashboard data over Socket.IO — replaces the REST polling the
// dashboards used to do.
//
// Lifecycle per mount:
//   1. grab the shared socket (same one chat/support uses) and connect it if needed
//   2. listen for dashboard:snapshot / :update / :error
//   3. emit dashboard:subscribe { scope, params }; re-emit whenever params change
//   4. on unmount: emit dashboard:unsubscribe and remove ONLY our listeners
//      (never disconnect — that would kill the shared chat socket)
//
// Resilience: if the handshake wasn't authenticated yet, a snapshot never
// arrives, or the socket drops, we quietly re-handshake / reconnect with backoff
// instead of showing a "please refresh" banner. socket.io's own transport
// reconnection runs in parallel for plain network drops.
//
// `params` is serialized to decide when to re-subscribe, so callers can pass a
// fresh object literal each render without causing an infinite loop.
//
// Returns: { data, loading, error, connected }
export function useDashboardSocket(scope, params, { enabled = true, timeoutMs = 15000 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);

  // Keep the latest params in a ref so the connect handler always subscribes
  // with current values without being part of the effect's dependency list.
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const paramsKey = JSON.stringify(params ?? {});
  const timeoutRef = useRef(null);
  // Once we have data, filter changes refresh silently (stale-while-revalidate)
  // instead of flashing skeletons across the whole dashboard.
  const hasDataRef = useRef(false);
  // Auto-reconnect bookkeeping: count attempts + a single pending retry timer.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const MAX_RETRIES = 10;

  useEffect(() => {
    if (!enabled || !scope) return undefined;

    const socket = getSocket();
    if (!socket) {
      setError({ code: 'NO_SOCKET', message: 'Socket unavailable (not authenticated)' });
      setLoading(false);
      return undefined;
    }

    const clearPendingTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const clearRetry = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const subscribe = () => {
      setLoading(!hasDataRef.current); // only the first load shows a spinner
      setError(null);
      socket.emit('dashboard:subscribe', { scope, params: paramsRef.current });
      clearPendingTimeout();
      timeoutRef.current = setTimeout(() => {
        // No snapshot in time — retry quietly instead of asking the user to
        // refresh. Keep any existing data on screen.
        setLoading(false);
        scheduleReconnect();
      }, timeoutMs);
    };

    // Quietly retry the subscription. The socket is SHARED with chat/support, so
    // we NEVER disconnect it — doing so tore down chat and spammed reconnects. We
    // only re-ask, or reconnect it if it happens to be down. Backs off; gives up
    // after MAX_RETRIES (a fresh snapshot resets the counter).
    const scheduleReconnect = () => {
      if (retryCountRef.current >= MAX_RETRIES) return;
      retryCountRef.current += 1;
      const delay = Math.min(800 * retryCountRef.current, 6000);
      clearRetry();
      retryTimerRef.current = setTimeout(() => {
        try {
          if (!socket.connected) socket.connect(); // reconnect the shared socket if down — never disconnect it
          else subscribe(); // connected — just re-ask
        } catch (e) {
          /* noop */
        }
      }, delay);
    };

    const onSnapshot = (payload) => {
      if (!payload || payload.scope !== scope) return;
      clearPendingTimeout();
      clearRetry();
      retryCountRef.current = 0; // healthy again
      hasDataRef.current = true;
      setData(payload.data);
      setLoading(false);
      setError(null);
    };
    const onUpdate = (payload) => {
      if (!payload || payload.scope !== scope) return;
      // Live push from a data change — refresh silently, no loading flicker.
      clearPendingTimeout();
      retryCountRef.current = 0;
      hasDataRef.current = true;
      setData(payload.data);
      setError(null);
    };
    const onError = () => {
      clearPendingTimeout();
      setLoading(false);
      // Any server error (incl. UNAUTHENTICATED): quietly re-subscribe with backoff.
      // We never disconnect — the socket is shared with chat/support. No banner.
      scheduleReconnect();
    };
    const onConnect = () => {
      setConnected(true);
      subscribe(); // (re)subscribe on connect AND on every reconnect
    };
    const onDisconnect = () => {
      setConnected(false);
      // The shared socket's own reconnection manages the lifecycle; on reconnect,
      // onConnect re-subscribes. We don't fight it here (no forced reconnect that
      // could battle chat/support over the shared connection).
    };

    socket.on('dashboard:snapshot', onSnapshot);
    socket.on('dashboard:update', onUpdate);
    socket.on('dashboard:error', onError);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (socket.connected) {
      setConnected(true);
      subscribe();
    } else {
      socket.connect();
    }

    return () => {
      clearPendingTimeout();
      clearRetry();
      socket.off('dashboard:snapshot', onSnapshot);
      socket.off('dashboard:update', onUpdate);
      socket.off('dashboard:error', onError);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.emit('dashboard:unsubscribe');
      // Intentionally NOT disconnecting — the socket is shared with chat/support.
    };
  }, [scope, paramsKey, enabled, timeoutMs]);

  return { data, loading, error, connected };
}

export default useDashboardSocket;
